<template>
  <div v-if="business">
    <PdfChunkRenderer
      v-for="(chunk, index) in mainChunks" 
      :key="index"
      :ref="`chunkRenderer-${index}`"
      :chunk-data="chunk"
      :business="business"
      :logo="logo"
      :keymaster="keymaster"
    />
  </div>
</template>

<script>
import { PDFDocument } from 'pdf-lib';
// Assuming PdfChunkRenderer.vue is imported correctly
import PdfChunkRenderer from './PdfChunkRenderer.vue'; 

export default {
  components: {
    PdfChunkRenderer,
  },
  data() {
    return {
      // Data variables to store loaded information
      logo: null,
      business: null,
      keymaster: null, // Will be set in mounted()
      
      // The array to hold the data chunks (size 10)
      mainChunks: [], 
      
      // New data fields to store the IDs, fetched from localStorage once
      businessId: null,
      branchId: null,
    }
  },
  async mounted() {
    // 1. Get IDs and Keymaster from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    
    this.keymaster = user.keymaster || "";
    this.businessId = user.businessid || "";
    this.branchId = user.branch || "";

    // 2. Fetch Business Info
    try {
      if (this.businessId && this.branchId) {
        let [businessInfo] = await this.$API.branches.findSpecificBranch({ 
          branch: this.branchId, 
          business: this.businessId 
        });
        this.business = businessInfo;

        // 3. Fetch Logo
        let logoUrl = businessInfo.BusinessLogoPath;
        if (logoUrl) {
          const { imageBase64 } = await this.$API.utilities.getImageInBase64({ file: logoUrl });
          this.logo = imageBase64;
        }
      }
    } catch (e) {
      console.error("Error loading business info or logo:", e);
      this.logo = null;
      this.business = null;
    }
  },
  methods: {
    /**
     * Chunks an array into smaller arrays of a specified size.
     */
    chunkArray(arr, chunkSize) {
      if (!arr || !Array.isArray(arr)) return [];
      const chunkedArray = [];
      for (let i = 0; i < arr.length; i += chunkSize) {
        chunkedArray.push(arr.slice(i, i + chunkSize));
      }
      return chunkedArray;
    },

    /**
     * Main function to chunk data, and start the generation/merge process.
     * @param {Array} data - The full list of items to be converted to PDF.
     */
    async generateReport (data) {
      if (!this.business) {
         console.error("Business information is not yet loaded or is unavailable.");
         return null;
      }
      if (!data || data.length === 0) {
        return null;
      }

      try {
        // 1. Initial Data Chunking (Max 10 items per chunk)
        // Set the data to mainChunks to trigger the v-for rendering of PdfChunkRenderer components
        this.mainChunks = this.chunkArray(data, 10);
        
        // Wait for components to be created and mounted
        await this.$nextTick(); 
        
        // 2. Orchestrate Parallel Processing (Max 2 concurrent jobs)
        
        // Get references to all the chunk renderer components
        // Ensure we handle $refs as an array if multiple components are rendered with the same ref name
        const renderers = this.mainChunks.map((_, index) => this.$refs[`chunkRenderer-${index}`]?.[0]).filter(Boolean);
        
        if (renderers.length === 0) {
            console.warn("No chunk renderers found after mounting. Check mainChunks data.");
            return;
        }

        // Concurrency-controlled processing: run up to `maxConcurrent` renderers at once.
        const maxConcurrent = 2;
        const pdfBlobs = [];

        // active holds promises that resolve to { index, blob }
        const active = new Set();

        // Helper to drain a finished promise result into pdfBlobs and remove it from `active`
        const processFinished = (result) => {
          if (!result) return;
          const { index, blob, error } = result;
          if (error) {
            console.error(`Renderer ${index} failed:`, error);
            pdfBlobs[index] = null;
          } else {
            pdfBlobs[index] = blob;
          }
          for (const p of active) {
            if (p._pdfIndex === index) {
              active.delete(p);
              break;
            }
          }
        };

        // Start renderers, enforcing concurrency
        for (let i = 0; i < renderers.length; i++) {
          // If we've hit concurrency limit, wait for one to finish first
          if (active.size >= maxConcurrent) {
            const result = await Promise.race(Array.from(active));
            processFinished(result);
            console.log(`RESOLVED (while waiting): ${result?.index}, active now ${active.size}`);
          }

          const renderer = renderers[i];

          // Wrap each renderer promise so it resolves to { index, blob } or { index, error }
          const wrapped = renderer.generatePdf()
            .then(blob => ({ index: i, blob }))
            .catch(error => ({ index: i, error }));
          // tag for easy lookup when removing from Set
          wrapped._pdfIndex = i;

          active.add(wrapped);
          console.log(`ADD PROMISE: ${i}, active ${active.size}`);
        }

        // Drain remaining active promises
        while (active.size > 0) {
          const result = await Promise.race(Array.from(active));
          processFinished(result);
          console.log(`RESOLVED (drain): ${result?.index}, active now ${active.size}`);
        }
        
        // 3. Merge All PDF Blobs using pdf-lib
        const mergedPdf = await PDFDocument.create();
        for (const blob of pdfBlobs) {
            const arrayBuffer = await blob.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            
            const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
            copiedPages.forEach((page) => mergedPdf.addPage(page));
        }

        // 4. Final Output
        const finalPdfBytes = await mergedPdf.save();
        const finalPdfBlob = new Blob([finalPdfBytes], { type: 'application/pdf' });
        
        // Open the final PDF in a new tab
        const pdfUrl = URL.createObjectURL(finalPdfBlob);
        window.open(pdfUrl, "_blank");
        
        // Revoke URL after a delay for memory cleanup
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);

      } catch(err) {
        console.error("Error in generateReport:", err);
      } finally {
        // Clear chunks to remove the rendering components from the DOM
        this.mainChunks = [];
      }
    },
  },
};
</script>

<style scoped>
/* No styles needed here, as the styling is in PdfChunkRenderer.vue */
</style>